<template>
    <q-table
        id="changeover-product-table"
        :rows="report"
        :columns="table.columns"
        :pagination="pagination"
        :hide-bottom="report.length > 0"
        :table-class="{ 'sticky-table': report.length > 0 }"
        row-key="id"
        class="no-shadow border-grey"
        @update:pagination="updatePagination($event)"
        :no-data-label="
            $t('No changeovers were found for the selected timeframe.')
        "
    >
        <template v-slot:header="props">
            <q-tr :props="props" v-if="report.length > 0">
                <q-th v-for="col in props.cols" :key="col.name" :props="props">
                    {{ col.label }}
                    <q-tooltip style="font-size: 14px">
                        {{ tooltipText[col.name] }}
                    </q-tooltip>
                </q-th>
            </q-tr>
        </template>
        <template v-slot:body-cell-previousProductName="props">
            <q-td class="formatted-cell ellipsis">
                {{ props.row.previousProductName }}
            </q-td>
        </template>
        <template v-slot:body-cell-nextProductName="props">
            <q-td class="formatted-cell ellipsis">
                {{ props.row.nextProductName }}
            </q-td>
        </template>
    </q-table>
</template>

<script>
import { convertDurationToMinutes } from '@utils/time.js';

export default {
    name: 'ChangeoverProductTable',
    emits: ['update:pagination'],
    props: {
        report: {
            type: Object,
            required: true
        },
        pagination: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            tooltipText: {
                previousProductName: this.$t(
                    'The product running on the line prior to the changeover'
                ),
                nextProductName: this.$t(
                    'The product running on the line following the changeover'
                ),
                eventsCount: this.$t(
                    'The number of changeovers between this pair of products'
                ),
                averageDuration: this.$t(
                    'The average duration of a changeover between this pair of products'
                ),
                totalAccumulatedExpectedDuration: this.$t(
                    'The total expected duration of all changeovers between this pair of products'
                ),
                totalAccumulatedDuration: this.$t(
                    'The total actual duration of all changeovers between this pair of products'
                ),
                totalLoss: this.$t(
                    'The difference between the total expected and total actual duration of all changeovers between this pair of products'
                ),
                averageLoss: this.$t(
                    'The total loss averaged over the number of changeovers between this pair of products'
                )
            }
        };
    },
    computed: {
        table() {
            return {
                columns: [
                    {
                        name: 'previousProductName',
                        field: 'previousProductName',
                        label: this.$t('Previous Product'),
                        align: 'left',
                        sortable: true
                    },
                    {
                        name: 'nextProductName',
                        field: 'nextProductName',
                        label: this.$t('Next Product'),
                        align: 'left',
                        sortable: true
                    },
                    {
                        name: 'eventsCount',
                        field: 'eventsCount',
                        label: this.$t('Number of Events'),
                        align: 'left',
                        sortable: true
                    },
                    {
                        name: 'averageDuration',
                        field: 'averageDuration',
                        label: this.$t('Average Duration (min)'),
                        align: 'left',
                        sortable: true,
                        format: val => this.convertDurationToMinutes(val)
                    },
                    {
                        name: 'totalAccumulatedExpectedDuration',
                        field: 'totalAccumulatedExpectedDuration',
                        label: this.$t('Total Expected Duration (min)'),
                        align: 'left',
                        sortable: true,
                        format: val => this.convertDurationToMinutes(val)
                    },
                    {
                        name: 'totalAccumulatedDuration',
                        field: 'totalAccumulatedDuration',
                        label: this.$t('Total Accumulated Duration (min)'),
                        align: 'left',
                        sortable: true,
                        format: val => this.convertDurationToMinutes(val)
                    },
                    {
                        name: 'totalLoss',
                        field: 'totalLoss',
                        label: this.$t('Total Loss (min)'),
                        align: 'left',
                        sortable: true,
                        format: val => this.convertDurationToMinutes(val)
                    },
                    {
                        name: 'averageLoss',
                        field: 'averageLoss',
                        label: this.$t('Average Loss (min)'),
                        align: 'left',
                        sortable: true,
                        format: val => this.convertDurationToMinutes(val)
                    }
                ]
            };
        }
    },
    methods: {
        convertDurationToMinutes,
        /**
         * Emits an event to update the pagination state.
         * @param {Object} pagination - The new pagination data to be emitted.
         */
        updatePagination(pagination) {
            this.$emit('update:pagination', pagination);
        }
    }
};
</script>

<style lang="scss" scoped>
:deep(.q-table__top) {
    background-color: $secondary-white;
}
:deep(.formatted-cell) {
    min-width: 250px;
    max-width: 250px;
}
</style>
